import { Form, Formik, FormikProps } from 'formik';
import { useMemo, useState } from 'react';

import { FormButton } from '../../../_core/form/button/form-button.component';
import { FormActionsRow } from '../../../_core/form/input/form-actions-row';
import { findFilesToDownload } from '../../request/request.utils';
import {
  DownloadableAssetType,
  IDownloadableAsset,
  IDownloadRequestAssetsFormProps,
  SelectedDownload
} from './download-request-assets.types';
import { DownloadRequestAssetsGridColumn } from './download-request-assets-grid-column.component';
import { ISectionData } from '../../../_editor/section/section.types';
import { letterize } from '../../../_editor/_core/local-title-block/local-title-block.utils';
import { ALL, WHOLE_REQUEST } from './download-request-assets.const';
import { getName } from '../../../../utils/name.utils';

export const DownloadRequestAssetsForm = ({ onBack, onSubmit, request }: IDownloadRequestAssetsFormProps) => {
  const [selected, setSelected] = useState<SelectedDownload[]>([]);

  const sectionOptions = useMemo(
    () => [
      { name: `0 - Request - ${(getName(request.assignedTo) || request.name) ?? ''}.pdf`, id: WHOLE_REQUEST },
      ...(request.blocks?.map(
        (b, i) =>
          ({
            id: b.id,
            name: `${letterize(i + 1).toUpperCase()} - ${(b.data as unknown as ISectionData).label}.pdf`
          } as IDownloadableAsset)
      ) ?? [])
    ],
    [request.assignedTo, request.blocks, request.name]
  );

  const { provided, uploads } = useMemo(
    () => ({
      provided: findFilesToDownload({
        blocks: request.blocks,
        fileType: 'docProvide',
        forceLabelsOnDocProvide: true
      }).map((itemData) => ({ id: itemData.fileId ?? '', name: itemData.label })),
      uploads: findFilesToDownload({ blocks: request.blocks, fileType: 'upload' }).map((itemData) => ({
        id: itemData.fileId ?? '',
        name: itemData.label
      }))
    }),
    [request.blocks]
  );

  const fileOptions = useMemo(
    () => ({
      [DownloadableAssetType.SECTION]: sectionOptions,
      [DownloadableAssetType.UPLOAD]: uploads,
      [DownloadableAssetType.PROVIDED_DOC]: provided
    }),
    [provided, sectionOptions, uploads]
  );

  const onSelect = ({ id, index, fileName, type }: SelectedDownload) => {
    setSelected((prev) => {
      const found = prev.findIndex((p) => p.id === id && p.index === index && p.type === type);

      // Handle toggle all of type
      if (id === ALL) {
        // If toggle off, then remove all of previous
        const newSelected = [...prev.filter((p) => p.type !== type)];
        if (found > -1) return newSelected;

        // Otherwise add all of type
        return [
          ...newSelected,
          { id: ALL, fileName, type },
          ...fileOptions[type].map((o, i) => ({ id: o.id, fileName: o.name, index: i, type }))
        ];
      }

      // Handle toggle single option, if value exists in curr list then remove it. Otherwise add it.
      if (found > -1) {
        // Filter out toggled item and the ALL value of that type (if selected)
        const newSelected = [...prev];
        newSelected.splice(found, 1);
        return newSelected.filter((s) => s.id !== ALL || s.type !== type);
      }

      const newSelected = [...prev, { id, index, fileName, type }];

      // If all options of type are selected then toggle ALL option for type
      if (fileOptions[type].length === newSelected.filter((s) => s.type === type).length)
        newSelected.push({ id: ALL, fileName, type });

      return newSelected;
    });
  };

  return (
    <Formik
      initialValues={{}}
      onSubmit={() => {
        const withoutAll = selected.filter((s) => s.id !== ALL);

        onSubmit({
          assets: withoutAll
            .filter((s) => s.type !== DownloadableAssetType.SECTION)
            .map((s) => ({ id: s.id, fileName: s.fileName })),
          sections: withoutAll
            .filter((s) => s.id !== WHOLE_REQUEST && s.type === DownloadableAssetType.SECTION)
            .map((s) => ({ id: s.id, fileName: s.fileName })),
          requestFileName: withoutAll.find((s) => s.id === WHOLE_REQUEST && s.type === DownloadableAssetType.SECTION)
            ?.fileName
        });
      }}
      validateOnBlur={false}
    >
      {(props: FormikProps<{ _?: void }>) => (
        <Form action="#" method="POST" className="w-full mx-auto space-y-4 mt-10">
          <div className="w-full grid grid-flow-col grid-cols-auto gap-4 justify-center">
            <DownloadRequestAssetsGridColumn
              title="Sections"
              files={sectionOptions}
              onSelect={onSelect}
              selected={selected}
              type={DownloadableAssetType.SECTION}
            />
            <DownloadRequestAssetsGridColumn
              title="Uploads"
              files={uploads}
              onSelect={onSelect}
              selected={selected}
              type={DownloadableAssetType.UPLOAD}
            />
            <DownloadRequestAssetsGridColumn
              title="Provided"
              files={provided}
              onSelect={onSelect}
              selected={selected}
              type={DownloadableAssetType.PROVIDED_DOC}
            />
          </div>
          <FormActionsRow>
            {!!onBack && <FormButton type="button" onPress={onBack} text="Cancel" variant="secondary" />}
            <FormButton
              loading={props.isSubmitting}
              disabled={props.isSubmitting || !selected.length}
              text="Download"
            />
          </FormActionsRow>
        </Form>
      )}
    </Formik>
  );
};
